import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE || 'http://localhost/school_sms/api';

const api = axios.create({ baseURL: BASE });

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem('refresh_token');
        if (!refresh) throw new Error('No refresh token');
        const { data } = await axios.post(`${BASE}/auth/refresh.php`, { refresh_token: refresh });
        localStorage.setItem('access_token', data.data.access_token);
        original.headers.Authorization = `Bearer ${data.data.access_token}`;
        return api(original);
      } catch {
        localStorage.clear();
        window.location.href = '/';
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Convenience wrappers ─────────────────────────────────────
export const authApi = {
  login:          (data)  => api.post('/auth/login.php', data),
  parentLogin:    (data)  => api.post('/auth/parent-login.php', data),
  logout:         (token) => api.post('/auth/logout.php', { refresh_token: token }),
  changePassword: (data)  => api.post('/auth/change-password.php', data),
  forgotPassword: (data)  => api.post('/auth/forgot-password.php', data),
  resetPassword:  (data)  => api.post('/auth/reset-password.php', data),
};

export const schoolApi = {
  getInfo: (slug) => api.get(`/school/info.php?slug=${slug}`),
};

export const superAdminApi = {
  getStats:       ()         => api.get('/super-admin/stats.php'),
  getSchools:     (params)   => api.get('/super-admin/schools.php', { params }),
  createSchool:   (data)     => api.post('/super-admin/schools.php', data),
  setStatus:      (data)     => api.put('/super-admin/school-status.php', data),
};

export const adminApi = {
  getStats:       ()         => api.get('/admin/stats.php'),
  getSettings:    ()         => api.get('/admin/settings.php'),
  updateSettings: (data)     => api.put('/admin/settings.php', data),
  uploadLogo:     (form)     => api.post('/admin/upload-logo.php', form),
  // Grades
  getGrades:      ()         => api.get('/admin/grades.php'),
  addGrade:       (d)        => api.post('/admin/grades.php', d),
  updateGrade:    (d)        => api.put('/admin/grades.php', d),
  deleteGrade:    (id)       => api.delete(`/admin/grades.php?id=${id}`),
  // Subjects
  getSubjects:    (p)        => api.get('/admin/subjects.php', { params: p }),
  getSubjectsWithGrades: ()  => api.get('/admin/subjects.php?action=with-grades'),
  getGradeSubjects: (gId)    => api.get(`/admin/subjects.php?action=grade-subjects&grade_id=${gId}`),
  addSubject:     (d)        => api.post('/admin/subjects.php', d),
  updateSubject:  (d)        => api.put('/admin/subjects.php', d),
  linkSubject:    (d)        => api.put('/admin/subjects.php?action=link', d),
  unlinkSubject:  (d)        => api.put('/admin/subjects.php?action=unlink', d),
  deleteSubject:  (id)       => api.delete(`/admin/subjects.php?id=${id}`),
  // Teachers
  getTeachers:    ()         => api.get('/admin/teachers.php'),
  getTeacher:     (id)       => api.get(`/admin/teachers.php?id=${id}`),
  addTeacher:     (d)        => api.post('/admin/teachers.php', d),
  updateTeacher:  (d)        => api.put('/admin/teachers.php', d),
  deactivateTeacher:(id)     => api.delete(`/admin/teachers.php?id=${id}`),
  // Learners
  getLearners:    (p)        => api.get('/admin/learners.php', { params: p }),
  addLearner:     (d)        => api.post('/admin/learners.php', d),
  updateLearner:  (d)        => api.put('/admin/learners.php', d),
  // Notices
  getNotices:     ()         => api.get('/admin/notices.php'),
  addNotice:      (d)        => api.post('/admin/notices.php', d),
  updateNotice:   (d)        => api.put('/admin/notices.php', d),
  deleteNotice:   (id)       => api.delete(`/admin/notices.php?id=${id}`),
  // Terms
  getTerms:       ()         => api.get('/admin/terms.php'),
  addTerm:        (d)        => api.post('/admin/terms.php', d),
  updateTerm:     (d)        => api.put('/admin/terms.php', d),
  // Library
  getBooks:       (p)        => api.get('/admin/library.php', { params: p }),
  addBook:        (form)     => api.post('/admin/library.php', form),
  deleteBook:     (id)       => api.delete(`/admin/library.php?id=${id}`),
  getCategories:  ()         => api.get('/admin/library.php?action=categories'),
  addCategory:    (d)        => api.post('/admin/library.php?action=categories', d),
  // Fees
  getFeeStructures: ()       => api.get('/admin/fees.php?action=structures'),
  addFeeStructure:  (d)      => api.post('/admin/fees.php?action=structures', d),
  getPayments:      (lid)    => api.get(`/admin/fees.php?action=payments&learner_id=${lid}`),
  addPayment:       (d)      => api.post('/admin/fees.php?action=payments', d),
  getOutstanding:   ()       => api.get('/admin/fees.php?action=outstanding'),
  // Events
  getEvents:      (p)        => api.get('/admin/events.php', { params: p }),
  addEvent:       (d)        => api.post('/admin/events.php', d),
  deleteEvent:    (id)       => api.delete(`/admin/events.php?id=${id}`),
};

export const teacherApi = {
  // Attendance
  getAttendance:  (gId, month) => api.get(`/teacher/attendance.php?grade_id=${gId}&month=${month}`),
  saveAttendance: (d)          => api.post('/teacher/attendance.php', d),
  bulkAttendance: (d)          => api.put('/teacher/attendance.php', d),
  // Assessments
  getAssessments: (p)          => api.get('/teacher/assessments.php', { params: p }),
  addAssessment:  (d)          => api.post('/teacher/assessments.php', d),
  deleteAssessment:(id)        => api.delete(`/teacher/assessments.php?id=${id}`),
  // Marks
  getMarks:       (p)          => api.get('/teacher/marks.php', { params: p }),
  saveMarks:      (d)          => api.post('/teacher/marks.php', d),
  // Materials
  getMaterials:   (p)          => api.get('/teacher/materials.php', { params: p }),
  uploadMaterial: (form)       => api.post('/teacher/materials.php', form),
  deleteMaterial: (id)         => api.delete(`/teacher/materials.php?id=${id}`),
  // Timetable
  getTimetable:   (p)          => api.get('/teacher/timetable.php', { params: p }),
  addTimetable:   (d)          => api.post('/teacher/timetable.php', d),
  deleteTimetable:(id)         => api.delete(`/teacher/timetable.php?id=${id}`),
  // Behaviour
  getBehaviour:   (lid)        => api.get(`/teacher/behaviour.php?learner_id=${lid}`),
  addBehaviour:   (d)          => api.post('/teacher/behaviour.php', d),
  // Notices (reuse admin endpoint for school-wide)
  getNotices:      ()           => api.get('/admin/notices.php'),
  addNotice:       (d)          => api.post('/admin/notices.php', d),
  // Class notices (grade-scoped)
  getClassNotices: (p)          => api.get('/teacher/class-notices.php', { params: p }),
  addClassNotice:  (d)          => api.post('/teacher/class-notices.php', d),
  deleteClassNotice:(id)        => api.delete(`/teacher/class-notices.php?id=${id}`),
  // Report cards
  getReportCard:  (p)          => api.get('/teacher/report-cards.php', { params: p }),
  saveReportCard: (d)          => api.post('/teacher/report-cards.php', d),
  // Messages
  getMessages:    (type)       => api.get(`/teacher/messages.php?type=${type}`),
  sendMessage:    (d)          => api.post('/teacher/messages.php', d),
  markRead:       (id)         => api.put('/teacher/messages.php', { message_id: id }),
  // Subjects / Grades (read-only for teacher)
  getSubjects:    (p)          => api.get('/admin/subjects.php', { params: p }),
  getGradeSubjects: (gId)      => api.get(`/admin/subjects.php?action=grade-subjects&grade_id=${gId}`),
  getGrades:      ()           => api.get('/admin/grades.php'),
};

export const learnerApi = {
  getDashboard:  ()                    => api.get('/learner/dashboard.php'),
  getProfile:    ()                    => api.get('/learner/profile.php'),
  updateProfile: (d)                   => api.put('/learner/profile.php', d),
  uploadAvatar:  (form)                => api.post('/learner/profile.php', form),
  getSubjects:   ()                    => api.get('/learner/subjects.php'),
  getAttendance: (month)               => api.get(`/learner/attendance.php?month=${month}`),
  getMarks:      (termId, subjectId)   => api.get('/learner/marks.php', { params: { term_id: termId || undefined, subject_id: subjectId || undefined } }),
  getMaterials:  (subjectId)           => api.get('/learner/materials.php', { params: { subject_id: subjectId || undefined } }),
  getNotices:    ()                    => api.get('/learner/notices.php'),
  getTimetable:  ()                    => api.get('/learner/timetable.php'),
};

export const analyticsApi = {
  getSchool:     (termId)              => api.get(`/analytics/school.php${termId?`?term_id=${termId}`:''}`),
  getTeacher:    (p)                   => api.get('/analytics/teacher.php', { params: p }),
  getLearner:    (learnerId, termId)   => api.get(`/analytics/learner.php?learner_id=${learnerId}${termId?`&term_id=${termId}`:''}`),
  getParent:     (learnerId)           => api.get(`/analytics/parent.php?learner_id=${learnerId}`),
  getReportCard: (learnerId, termId, token) => {
    const t = token || localStorage.getItem('access_token');
    return `http://localhost/school_sms/api/analytics/report-card-pdf.php?learner_id=${learnerId}${termId?`&term_id=${termId}`:''}&_token=${t}`;
  },
};

export const parentApi = {
  getChildren:    ()           => api.get('/parent/children.php'),
  getProgress:    (lid)        => api.get(`/parent/child-progress.php?learner_id=${lid}`),
  getNotices:     ()           => api.get('/parent/notices.php'),
  getMessages:    (type)       => api.get(`/parent/messages.php?type=${type}`),
  sendMessage:    (d)          => api.post('/parent/messages.php', d),
  markRead:       (id)         => api.put('/parent/messages.php', { message_id: id }),
};
